/**
 * TaskDescriptionSection - Descrição rica + Anexos da tarefa
 * 
 * Replica o visual do DealDescriptionAndAttachments:
 * - Header com ícone Menu + título + botões de fonte e edição
 * - Modo leitura com RichTextEditor readOnly + thumbnails de anexos
 * - Modo edição com RichTextEditor + área de upload + lista de anexos
 * - Lightbox para visualização de imagens
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial replicando padrão do deal
 * RELEASE: 2026-04-07 v2.0.0 - Adicionada seção de anexos (padrão DealDescriptionAndAttachments)
 */
import React, { useState, useCallback, useRef } from 'react';
import {
  Menu,
  Pencil,
  X,
  Save,
  Loader2,
  AArrowUp,
  Paperclip,
  Download,
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { ImageLightbox, MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve preview interno x download conforme o tipo do arquivo
import { canPreviewInline, resolvePreviewMediaType, downloadAttachmentFile } from '@/lib/attachment-preview';
import { useTaskAttachments } from '@/hooks/useTaskAttachments';
import { useDownloadAllAsZip } from '@/hooks/useDownloadAllAsZip';
import { getFileIcon, getFileColor, formatFileSize } from '@/lib/file-utils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/** Props do componente */
interface TaskDescriptionSectionProps {
  /** ID da tarefa (necessário para hook de anexos) */
  taskId: string;
  content?: string | null;
  plainContent?: string | null;
  readOnly?: boolean;
  onSave: (html: string) => Promise<void>;
  className?: string;
  /** Oculta a área de anexos (reuso em outros módulos, ex.: Observação da remessa) */
  hideAttachments?: boolean;
  /** Título da seção (padrão "Descrição") */
  title?: string;
  /** Texto de apoio do editor */
  placeholder?: string;
}

/**
 * Item de anexo para modo edição (padrão DealDescriptionAndAttachments)
 */
function AttachmentListItem({
  attachment,
  onDelete,
  isDeleting,
}: {
  attachment: { id: string; file_name: string; file_type: string; file_size: number | null };
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const FileIcon = getFileIcon(attachment.file_type);
  const colorClasses = getFileColor(attachment.file_type);

  return (
    <div className="flex items-center justify-between p-2 bg-background rounded border">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className={cn('p-1.5 rounded', colorClasses)}>
          <FileIcon className="h-4 w-4" />
        </div>
        <span className="text-sm truncate">{attachment.file_name}</span>
      </div>
      <div className="flex items-center gap-2 ml-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatFileSize(attachment.file_size)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3 text-destructive" />
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Seção de descrição rica + anexos - padrão visual do DealDescriptionAndAttachments
 */
export function TaskDescriptionSection({
  taskId,
  content,
  plainContent,
  readOnly = false,
  onSave,
  className,
  hideAttachments = false,
  title = 'Descrição',
  placeholder = 'Adicione uma descrição detalhada da tarefa...',
}: TaskDescriptionSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Lightbox para imagens
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Referência para input de arquivo oculto
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hook de anexos da tarefa
  const {
    attachments,
    isLoading: isLoadingAttachments,
    uploadFile,
    deleteAttachment,
    isUploading,
  } = useTaskAttachments(hideAttachments ? undefined : taskId);

  const attachmentCount = attachments?.length || 0;

  // Hook para download ZIP de todos os anexos
  const { handleDownloadAll, isZipping } = useDownloadAllAsZip(attachments, 'anexos_tarefa.zip');

  // Tamanho de fonte persistido
  const [fontSize, setFontSize] = useState<'xs' | 'sm' | 'base'>(() => {
    const saved = localStorage.getItem('task-description-font-size');
    return (saved === 'xs' || saved === 'sm' || saved === 'base') ? saved : 'xs';
  });

  /** Atualiza tamanho de fonte e persiste */
  const handleFontSizeChange = useCallback((size: 'xs' | 'sm' | 'base') => {
    setFontSize(size);
    localStorage.setItem('task-description-font-size', size);
  }, []);

  // Conteúdo para exibição
  const displayContent = content || plainContent || '';
  const hasContent = Boolean(displayContent.trim());

  /** Iniciar edição */
  const handleStartEdit = useCallback(() => {
    setEditedContent(content || (plainContent ? `<p>${plainContent}</p>` : ''));
    setIsEditing(true);
  }, [content, plainContent]);

  /** Cancelar edição */
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditedContent('');
  }, []);

  /** Salvar alterações */
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      await onSave(editedContent);
      setIsEditing(false);
      setEditedContent('');
    } catch {
      // Erro tratado pelo componente pai
    } finally {
      setIsSaving(false);
    }
  }, [editedContent, onSave]);

  /** Abrir seletor de arquivos */
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** Processar arquivos selecionados */
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      try {
        await uploadFile(file);
      } catch {
        // Erro tratado pelo hook
      }
    }

    // Limpar input para permitir re-upload do mesmo arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadFile]);

  /** Deletar anexo */
  const handleDeleteAttachment = useCallback((attachmentId: string) => {
    setDeletingId(attachmentId);
    deleteAttachment(attachmentId);
    // Reset após um delay (o hook invalida queries)
    setTimeout(() => setDeletingId(null), 1000);
  }, [deleteAttachment]);

  /**
   * Clique em thumbnail de anexo
   * v1.x: abre o PREVIEW INTERNO (imagem/vídeo/PDF). Formatos que o navegador
   * não renderiza inline (Word, Excel, zip...) continuam sendo baixados.
   */
  const handleAttachmentClick = useCallback(async (attachment: typeof attachments[0]) => {
    const previewable = attachments.filter(a => canPreviewInline(a.file_type, (a as any).mime_type));
    if (canPreviewInline(attachment.file_type, (attachment as any).mime_type)) {
      const idx = previewable.findIndex(a => a.id === attachment.id);
      setLightboxIndex(idx >= 0 ? idx : 0);
      setLightboxOpen(true);
      return;
    }
    try {
      await downloadAttachmentFile(attachment.public_url, attachment.file_name);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  }, [attachments]);

  /** Itens de mídia para o preview interno (imagem, vídeo e documento/PDF) */
  const lightboxMediaItems: MediaItem[] = attachments
    .filter(a => canPreviewInline(a.file_type, (a as any).mime_type))
    .map(a => ({
      url: a.public_url,
      type: resolvePreviewMediaType(a.file_type, (a as any).mime_type) ?? 'document',
      name: a.file_name,
    }));

  return (
    <div className={cn('rounded-lg border bg-accent/30', className)}>
      {/* Header - idêntico ao DealDescriptionAndAttachments */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Menu className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">
            {isEditing ? `Editando ${title}` : title}
          </h4>
        </div>

        {isEditing ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleCancel}
              disabled={isSaving}
              title="Cancelar"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              className="h-7 w-7"
              onClick={handleSave}
              disabled={isSaving}
              title="Salvar"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            {/* Seletor de tamanho de fonte */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Tamanho da fonte"
                >
                  <AArrowUp className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-36 p-1">
                {([
                  { value: 'xs' as const, label: 'Pequena' },
                  { value: 'sm' as const, label: 'Média' },
                  { value: 'base' as const, label: 'Grande' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleFontSizeChange(opt.value)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors',
                      'hover:bg-accent',
                      fontSize === opt.value && 'bg-accent font-medium'
                    )}
                  >
                    <span className={cn(
                      'text-muted-foreground',
                      opt.value === 'xs' && 'text-xs',
                      opt.value === 'sm' && 'text-sm',
                      opt.value === 'base' && 'text-base',
                    )}>Aa</span>
                    <span className="text-xs">{opt.label}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>

            {/* Botão de edição */}
            {!readOnly && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleStartEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Conteúdo */}
      {isEditing ? (
        <>
          {/* Editor de texto rico */}
          <div className="p-3">
            <RichTextEditor
              content={editedContent}
              onChange={setEditedContent}
              placeholder={placeholder}
              minHeight="120px"
            />
          </div>

          {/* Área de anexos em modo edição */}
          {!hideAttachments && <div className="p-3 border-t border-border/50">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Anexos</span>
              {attachmentCount > 0 && (
                <span className="text-xs text-muted-foreground">({attachmentCount})</span>
              )}
            </div>

            {/* Área de upload compartilhada */}
            <AttachmentUploadArea
              onUploadClick={handleUploadClick}
              isUploading={isUploading}
              maxSizeLabel="Máx: 50MB por arquivo | Múltiplos permitidos"
              className="mb-3"
            />

            {/* Lista de arquivos anexados */}
            {attachmentCount > 0 && (
              <div className="space-y-2">
                {attachments?.map(attachment => (
                  <AttachmentListItem
                    key={attachment.id}
                    attachment={attachment}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    isDeleting={deletingId === attachment.id}
                  />
                ))}
              </div>
            )}
          </div>}
        </>
      ) : (
        <>
          {/* Descrição em modo visualização */}
          <div className="p-3">
            {hasContent ? (
              <div className="max-h-[200px] overflow-y-auto scrollbar-thin">
                <RichTextEditor
                  content={displayContent}
                  readOnly
                  showToolbar={false}
                  minHeight="60px"
                  fontSize={fontSize}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nenhuma {title.toLowerCase()} adicionada
              </p>
            )}
          </div>

          {/* Separador sutil entre descrição e anexos */}
          {!hideAttachments && <><div className="px-3">
            <div className="border-t border-border" />
          </div>

          {/* Anexos em modo visualização - padrão DealDescriptionAndAttachments */}
          <div className="p-3 pt-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Anexos ({attachmentCount})
                </span>
              </div>
              {/* Botão para baixar todos os anexos como ZIP */}
              {attachmentCount > 1 && (
                <button
                  type="button"
                  onClick={handleDownloadAll}
                  disabled={isZipping}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                >
                  {isZipping ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Download className="h-3 w-3" />
                  )}
                  <span>Baixar todos os arquivos...</span>
                </button>
              )}
            </div>

            {isLoadingAttachments ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : attachmentCount > 0 ? (
              <div className="flex flex-wrap gap-2">
                {attachments?.map(attachment => (
                  <AttachmentThumbnail
                    key={attachment.id}
                    attachment={attachment}
                    readOnly={readOnly}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    onClick={() => handleAttachmentClick(attachment)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Nenhum anexo
              </p>
            )}
          </div></>}
        </>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Lightbox para visualização de imagens */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />
    </div>
  );
}
